// src/pages/Home.jsx
import React, { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../components/layout/PublicLayout";
import Hero from "../components/sections/Hero";
import Projects from "../components/sections/Projects";
import Experience from "../components/sections/Experience";
import Skills from "../components/sections/Skills";
import Education from "../components/sections/Education";
import Gallery from "../components/sections/Gallery";
import Articles from "../components/sections/Articles";
import { usePortfolio } from "../contexts/PortfolioContext";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_TITLE,
  SITE_NAME,
  absoluteUrl,
  createPersonSchema,
  createWebsiteSchema,
} from "../utils/seo";

const Home = ({ isDownloading, handleDownload }) => {
  const { portfolioData, loading } = usePortfolio();
  const personalInfo = portfolioData.personalInfo || {};
  const location = useLocation();
  const canonicalUrl = absoluteUrl("/");
  const title = personalInfo.seoTitle || DEFAULT_TITLE;
  const description =
    personalInfo.seoDescription || personalInfo.bio || DEFAULT_DESCRIPTION;
  const imageUrl = absoluteUrl("/profilepic.png");
  const keywords = DEFAULT_KEYWORDS.join(", ");
  const personSchema = createPersonSchema(personalInfo);
  const websiteSchema = createWebsiteSchema();

  useLayoutEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "start" });
      }
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useLayoutEffect(() => {
    if (loading || !location.hash) return;
    const element = document.getElementById(location.hash.replace("#", ""));
    if (element) {
      element.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, [loading, location]);

  if (loading) {
    return (
      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <section className="public-section" aria-labelledby="loading-title">
          <div className="container">
            <div className="public-loading">
              <div className="progress" aria-hidden="true">
                <div className="indeterminate" />
              </div>
              <p id="loading-title">Loading the portfolio…</p>
            </div>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={description} />
        <meta name="author" content={personalInfo.name || "Aurio Rajaa"} />
        <meta name="keywords" content={keywords} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={`${personalInfo.name || "Aurio Rajaa"} profile photo`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@auriorajaa" />
        <meta name="twitter:creator" content="@auriorajaa" />
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>

      <PublicLayout
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      >
        <Hero
          isDownloading={isDownloading}
          handleDownload={handleDownload}
        />
        <Projects />
        <Experience />
        <Skills />
        <Education />
        <Gallery />
        <Articles />
      </PublicLayout>
    </div>
  );
};

export default Home;
